import React from 'react';
import { getFastener } from './fastenerLibrary';

interface FastenerSymbolProps {
  id: string;
  size?: number;
  color?: string;
  label?: string;
  showLabel?: boolean;
}

const ink = (color: string) => ({
  fill: 'none' as const,
  stroke: color,
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

function threadTicks(x0: number, x1: number, y: number, h: number, color: string) {
  const ticks: React.ReactNode[] = [];
  for (let x = x0; x <= x1; x += 2.1) {
    ticks.push(<line key={x} x1={x} y1={y - h / 2} x2={x + 1.4} y2={y + h / 2} stroke={color} strokeWidth={1.1} />);
  }
  return ticks;
}

function JointMark({ id, color }: { id: string; color: string }) {
  const s = ink(color);
  switch (id) {
    case 'peg':
      return <circle cx={20} cy={20} r={5.2} fill={color} />;
    case 'peg-pair':
      return (
        <g>
          <circle cx={14} cy={20} r={4.4} fill={color} />
          <circle cx={26} cy={20} r={4.4} fill={color} />
        </g>
      );
    case 'peg-triple':
      return (
        <g>
          <circle cx={10} cy={20} r={3.6} fill={color} />
          <circle cx={20} cy={20} r={3.6} fill={color} />
          <circle cx={30} cy={20} r={3.6} fill={color} />
        </g>
      );
    case 'peg-quad':
      return (
        <g>
          <circle cx={14} cy={14} r={3.6} fill={color} />
          <circle cx={26} cy={14} r={3.6} fill={color} />
          <circle cx={14} cy={26} r={3.6} fill={color} />
          <circle cx={26} cy={26} r={3.6} fill={color} />
        </g>
      );
    case 'washer-head-screw':
      return (
        <g>
          <circle cx={20} cy={20} r={10.4} {...s} />
          <circle cx={20} cy={20} r={6.2} {...s} />
          <circle cx={20} cy={20} r={2} fill={color} />
        </g>
      );
    case 'fully-threaded-screw':
      return (
        <g>
          <circle cx={20} cy={20} r={8.2} {...s} />
          {[0, 60, 120].map(deg => (
            <line
              key={deg}
              x1={20 + 5.4 * Math.cos((deg * Math.PI) / 180)}
              y1={20 + 5.4 * Math.sin((deg * Math.PI) / 180)}
              x2={20 + 5.4 * Math.cos(((deg + 180) * Math.PI) / 180)}
              y2={20 + 5.4 * Math.sin(((deg + 180) * Math.PI) / 180)}
              {...s}
            />
          ))}
        </g>
      );
    case 'tenon':
      return (
        <g>
          <rect x={6} y={14} width={12} height={12} {...s} />
          <rect x={18} y={16.5} width={16} height={7} {...s} fill={color} opacity={0.18} />
        </g>
      );
    case 'through-tenon':
      return (
        <g>
          <rect x={12} y={10} width={16} height={20} {...s} />
          <rect x={4} y={16.5} width={32} height={7} {...s} fill={color} opacity={0.16} />
        </g>
      );
    case 'stub-tenon':
      return (
        <g>
          <rect x={8} y={13} width={14} height={14} {...s} />
          <rect x={22} y={16.5} width={10} height={7} {...s} fill={color} opacity={0.18} />
        </g>
      );
    case 'haunched-tenon':
      return (
        <g>
          <rect x={6} y={12} width={12} height={16} {...s} />
          <path d="M18 14 H28 V19 H24 V26 H18 Z" {...s} fill={color} opacity={0.16} />
        </g>
      );
    case 'dovetail':
      return <polygon points="8,14 32,10 32,30 8,26" {...s} fill={color} opacity={0.16} />;
    case 'half-dovetail':
      return <polygon points="8,13 32,13 32,30 8,26" {...s} fill={color} opacity={0.16} />;
    case 'mortise':
      return <rect x={12} y={11} width={16} height={18} strokeDasharray="3 2" {...s} />;
    case 'housing':
      return (
        <g>
          <path d="M8 12 H32 V28 H8 Z" {...s} />
          <path d="M8 12 H32 V18 H8 Z" {...s} fill={color} opacity={0.2} />
        </g>
      );
    case 'scarf':
      return (
        <g>
          <path d="M6 26 L18 12 L34 12" {...s} />
          <path d="M6 28 L22 28 L34 14" {...s} />
        </g>
      );
    case 'half-lap':
      return (
        <g>
          <rect x={6} y={12} width={20} height={8} {...s} />
          <rect x={14} y={20} width={20} height={8} {...s} />
        </g>
      );
    case 'bridle':
      return (
        <g>
          <path d="M10 10 H18 V16 H30 V24 H18 V30 H10 Z" {...s} />
          <rect x={18} y={16} width={14} height={8} fill={color} opacity={0.16} />
        </g>
      );
    case 'birdsmouth':
      return (
        <g>
          <path d="M6 14 H34 V28 H6 Z" {...s} />
          <path d="M14 14 L20 24 L26 14" {...s} />
        </g>
      );
    case 'tusk-tenon':
      return (
        <g>
          <rect x={10} y={10} width={14} height={20} {...s} />
          <rect x={6} y={16.5} width={28} height={7} {...s} />
          <polygon points="28,13 34,20 28,27" {...s} fill={color} />
        </g>
      );
    default:
      return <circle cx={20} cy={20} r={5} fill={color} />;
  }
}

function BodyMark({ id, color }: { id: string; color: string }) {
  const s = ink(color);
  switch (id) {
    case 'peg-body':
      return (
        <g>
          <rect x={4} y={15} width={32} height={10} rx={5} fill={color} />
          <line x1={10} y1={15} x2={10} y2={25} stroke="#fff" strokeWidth={0.8} opacity={0.45} />
          <line x1={30} y1={15} x2={30} y2={25} stroke="#fff" strokeWidth={0.8} opacity={0.45} />
        </g>
      );
    case 'washer-head-screw-body':
      return (
        <g>
          <rect x={2} y={12} width={3.2} height={16} {...s} fill={color} />
          <rect x={5.2} y={14.2} width={3.4} height={11.6} {...s} />
          <rect x={8.6} y={17.4} width={8} height={5.2} {...s} />
          <polygon points="16.6,17.4 36,19.2 36,20.8 16.6,22.6" {...s} />
          {threadTicks(17, 35, 20, 5.4, color)}
        </g>
      );
    case 'fully-threaded-screw-body':
      return (
        <g>
          <rect x={2} y={13.5} width={4.2} height={13} {...s} fill={color} />
          <polygon points="6.2,17.2 34.5,19 36,20 34.5,21 6.2,22.8" {...s} />
          {threadTicks(7, 34, 20, 6.4, color)}
        </g>
      );
    case 'tenon-body':
      return (
        <g>
          <rect x={4} y={10} width={16} height={20} {...s} />
          <rect x={20} y={15} width={16} height={10} {...s} fill={color} opacity={0.16} />
        </g>
      );
    case 'through-tenon-body':
      return (
        <g>
          <rect x={12} y={8} width={16} height={24} {...s} />
          <rect x={3} y={16} width={34} height={8} {...s} fill={color} opacity={0.16} />
        </g>
      );
    case 'dovetail-body':
      return (
        <g>
          <rect x={3} y={11} width={12} height={18} {...s} />
          <polygon points="15,14 37,11 37,29 15,26" {...s} fill={color} opacity={0.16} />
        </g>
      );
    default:
      return <rect x={6} y={17} width={28} height={6} {...s} />;
  }
}

export function FastenerSymbol({ id, size = 32, color = '#1a1a1a', label, showLabel }: FastenerSymbolProps) {
  const def = getFastener(id);
  const group = def?.group ?? 'fastener';
  const caption = label ?? (showLabel ? def?.shortLabel : undefined);

  return (
    <svg width={size} height={size} viewBox="0 0 40 40" overflow="visible" aria-hidden>
      {group === 'body' ? <BodyMark id={id} color={color} /> : <JointMark id={id} color={color} />}
      {caption ? (
        <text
          x={20}
          y={group === 'body' ? 38 : 39}
          textAnchor="middle"
          fill={color}
          fontSize={5.4}
          fontFamily='Georgia, "Iowan Old Style", Palatino, serif'
          fontWeight={600}
        >
          {caption}
        </text>
      ) : null}
    </svg>
  );
}
